/**
 * Natural (intrinsic) pixel dimensions of an icon image.
 *
 * KML `<IconStyle>` publishes only a `scale`, never the image's dimensions, so
 * to render a placemark's own `<Icon>` at the footprint the legacy
 * `google.maps.KmlLayer` drew (native size times scale) we must measure the
 * image itself. `kmlSymbology` reads these to size each feature's icon.
 */
export type IconSize = { width: number; height: number };

// Measured sizes are stable per URL, so cache across layers and polling
// re-fetches — a directional AVL layer refreshing every few seconds should
// never re-download an icon just to re-measure it.
const cache = new Map<string, IconSize>();

const measure = (url: string): Promise<IconSize | null> =>
  new Promise(resolve => {
    const img = new Image();
    img.onload = () =>
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    img.onerror = () => resolve(null);
    img.src = url;
  });

/**
 * Measure the natural dimensions of every distinct icon URL, returning a lookup
 * keyed by URL. URLs that fail to load are omitted, so the caller falls back to
 * a default size for them.
 */
export const measureIconSizes = async (
  urls: readonly string[],
): Promise<Map<string, IconSize>> => {
  const distinct = [...new Set(urls)].filter(url => !cache.has(url));

  const measured = await Promise.all(
    distinct.map(async url => [url, await measure(url)] as const),
  );

  measured.forEach(([url, size]) => {
    if (size) {
      cache.set(url, size);
    }
  });

  return new Map(
    [...new Set(urls)].flatMap(url => {
      const size = cache.get(url);
      return size ? [[url, size] as const] : [];
    }),
  );
};
